import React, { useMemo } from 'react';
import { Box, Typography, Grid, Card, CardContent, Paper, Chip, Divider } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { EpokAnalysisService } from '../services/epok-analysis.service';

interface EpokAnalysisViewProps {
  session: any;
  language: string;
}

interface CategoryData {
  name: string;
  englishName: string;
  description: string;
  value: number;
}

// Helper function to get score-based text
const getScoreRangeText = (score: number, categoryTexts: any) => {
  if (score > 80) return categoryTexts.excellent || '';
  if (score > 60) return categoryTexts.good || '';
  if (score > 40) return categoryTexts.average || '';
  return categoryTexts.poor || '';
};

// Category name mappings
const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Executive' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress' },
  'principles': { hebrew: 'עקרונות בסיסיים', english: 'Principles' },
  'helpAcceptance': { hebrew: 'עזרה', english: 'Help' },
};


const EpokAnalysisView: React.FC<EpokAnalysisViewProps> = ({ session, language }) => {
  const { t, direction } = useLanguage();
  
  // Debug logging
  console.log('EpokAnalysisView - Session:', session);
  console.log('EpokAnalysisView - Session responses:', session?.responses);
  console.log('EpokAnalysisView - Language:', language);
  
  // Initialize the analysis service
  const analysisService = useMemo(() => new EpokAnalysisService(), []);
  
  // Analyze user responses if available
  const detailedAnalysis = useMemo(() => {
    if (session.responses && session.responses.length > 0) {
      return analysisService.analyzeResponses(
        session.responses, 
        language as 'he' | 'en',
        {
          commentTemplates: t.commentTemplates,
          categoryNames: t.categoryNames
        }
      );
    }
    return [];
  }, [session.responses, language, analysisService, t.commentTemplates, t.categoryNames]);
  
  // Generate detailed feedback report
  const detailedFeedback = useMemo(() => {
    if (session.responses && session.responses.length > 0) {
      return analysisService.generateDetailedFeedback(
        session.responses,
        language as 'he' | 'en',
        {
          commentTemplates: t.commentTemplates,
          categoryNames: t.categoryNames
        }
      );
    }
    return [];
  }, [session.responses, language, analysisService, t.commentTemplates, t.categoryNames]);
  
  // Debug the detailed feedback
  console.log('Detailed feedback length:', detailedFeedback.length);
  console.log('First 5 feedback items:', detailedFeedback.slice(0, 5));
  
  // Helper function to get category description based on score
  const getCategoryDescription = (categoryKey: string, score: number) => {
    const categoryTexts = t.epokCategories[categoryKey as keyof typeof t.epokCategories];
    if (!categoryTexts) return `${t.epokAnalysisUI.defaultScore} ${score}`;
    return getScoreRangeText(score, categoryTexts);
  };
  
  // Get actual category scores from session results or use defaults
  // Support both direct and admin session data structures
  const sessionResults = (session as any)?.results || session?.results;
  const categoryScores = sessionResults?.categoryScores || {};
  console.log('session:', session);
  console.log('sessionResults:', sessionResults);
  console.log('Category Scores:', categoryScores);
  
  // Show message if no results are available
  const hasCategoryScores = categoryScores && Object.keys(categoryScores).length > 0;
  if (!hasCategoryScores) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
          {t.epokAnalysisUI.dataNotAvailable}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t.epokAnalysisUI.dataUnavailableMessage}
        </Typography>
      </Box>
    );
  }
  
  
  // Build category data from actual scores using multilingual texts
  const categoryData: CategoryData[] = Object.entries(categoryScores).slice(0, 7).map(([categoryKey, score], index) => {
    // Map the index to our category names since we're taking the first 7
    const categoryKeys = Object.keys(categoryNames);
    const mappedCategoryKey = categoryKeys[index] || 'management';
    return {
      name: t.categoryNames[mappedCategoryKey as keyof typeof t.categoryNames] || categoryKey,
      englishName: categoryNames[mappedCategoryKey]?.english || categoryKey,
      description: getCategoryDescription(mappedCategoryKey, score as number),
      value: score as number
    };
  });
  
  // Get dynamic recommendations based on categories under 60
  const getTopRecommendations = () => {
    const categoryKeys = Object.keys(categoryNames);
    const categoriesUnder60 = Object.entries(categoryScores)
      .slice(0, 7)
      .map(([, score], index) => {
        // Use the index directly to map to category keys
        const categoryKey = categoryKeys[index] || categoryKeys[0];
        return { categoryKey, score: score as number, index };
      })
      .filter(({ score }) => score < 60)
      .sort((a, b) => a.score - b.score); // Sort by lowest scores first
    
    const recommendations: string[] = [];
    categoriesUnder60.forEach(({ categoryKey, score }) => {
      const categoryName = t.categoryNames[categoryKey as keyof typeof t.categoryNames];
      const categoryDescription = getCategoryDescription(categoryKey, score < 40 ? 30 : 50); // Use appropriate score range
      recommendations.push(`${categoryName}: ${categoryDescription}`);
    });
    
    return recommendations.slice(0, 5); // Max 5 total recommendations
  };

  // Calculate actual average from session data
  const average = session?.results?.totalScore || 
    (categoryData.length > 0 
      ? categoryData.reduce((sum, cat) => sum + cat.value, 0) / categoryData.length 
      : 0);
  
  // Calculate dynamic potential growth based on first 7 categories
  const calculatePotentialGrowth = () => {
    console.log('calculatePotentialGrowth');
    console.log('Available category scores:', categoryScores);
    
    // Take first 7 entries from the categoryScores object
    const allScores = Object.values(categoryScores) as number[];
    const first7Scores = allScores.slice(0, 7);
    
    console.log('First 7 category scores:', first7Scores);
    if (first7Scores.length === 0) return 100; // Default if no data
    
    // Calculate plug number (average of the first 7 categories)
    let plugNumber = first7Scores.reduce((sum, score) => sum + score, 0) / first7Scores.length;
    // floor to nearest whole 10 number
    plugNumber = Math.floor(plugNumber / 10) * 10;
    console.log('Plug Number (average of first 7):', plugNumber);
    
    if (plugNumber <= 0) return 9999; // Infinite growth potential from 0
    if (plugNumber >= 100) return 0; // No growth potential if already at 100%

    let growthPotential = 100 * (1 / (plugNumber / 100) - 1);
    // ceil to nearest whole 50 number
    growthPotential = Math.ceil(growthPotential / 50) * 50;
    
    console.log('Plug Number:', plugNumber, 'Growth Potential:', growthPotential);

    return Math.round(growthPotential);
  };
  
  const potentialGrowth = calculatePotentialGrowth();

  const getCategoryStyle = (value: number) => {
    if (value >= 60) return { backgroundColor: '#f0f9f4', borderColor: '#27ae60' };
    if (value >= 40) return { backgroundColor: '#fef9e7', borderColor: '#f39c12' };
    return { backgroundColor: '#fdf2f2', borderColor: '#e74c3c' };
  };

  return (
    <Box sx={{ direction: direction, fontFamily: 'Arial, sans-serif' }} data-epok-analysis="true">
      {/* Header */}
      <Typography 
        variant="subtitle1" 
        sx={{ 
          textAlign: 'center', 
          color: '#666', 
          mb: 4,
          fontSize: '1.2em'
        }}
      >
{t.epokAnalysisUI.subtitle}
      </Typography>

      {/* Growth Potential Infographic */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
          color: 'white',
          p: 2,
          borderRadius: 2,
          mb: 3,
          textAlign: 'center'
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h1" sx={{ fontSize: '2.5em', mb: 0.5 }}>📈</Typography>
            <Typography variant="h3" sx={{ fontSize: '2em', fontWeight: 'bold', mb: 0.5 }}>
              {potentialGrowth}%
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t.epokAnalysisUI.growthPotential}
            </Typography>
          </Grid>
          <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: '2em', opacity: 0.8 }}>→</Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Typography variant="h1" sx={{ fontSize: '2.5em', mb: 0.5 }}>🏢</Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: t.epokAnalysisUI.businessGrowth }} />
          </Grid>
          <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: '2em', opacity: 0.8 }}>→</Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
            <Typography variant="h1" sx={{ fontSize: '2.5em', mb: 0.5 }}>🎓</Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: t.epokAnalysisUI.organizationalKnowledge }} />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
{t.epokAnalysisUI.growthMessage.replace('{potential}', potentialGrowth.toString())}
      </Typography>

      {/* Symptoms Infographic - Show only if there are critical scores */}
      {average < 60 && (
        <Paper
          elevation={2}
          sx={{
            background: 'rgba(231, 76, 60, 0.1)',
            border: '2px solid rgba(231, 76, 60, 0.3)',
            p: 3,
            borderRadius: 3,
            mb: 4
          }}
        >
          <Grid container spacing={3}>
            {Object.values(categoryScores).some(score => (score as number) < 50) && (
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', backgroundColor: 'rgba(231, 76, 60, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h1" sx={{ fontSize: '3em', color: '#e74c3c', mb: 1 }}>💸</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e74c3c', mb: 0.5 }}>
                      {t.epokAnalysisUI.cashFlow}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {t.epokAnalysisUI.improvementNeeded}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {Object.values(categoryScores).some(score => (score as number) < 40) && (
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', backgroundColor: 'rgba(231, 76, 60, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h1" sx={{ fontSize: '3em', color: '#e74c3c', mb: 1 }}>🚨</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e74c3c', mb: 0.5 }}>
                      {t.epokAnalysisUI.emergencyMode}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>{t.epokAnalysisUI.constantState}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {Object.values(categoryScores).some(score => (score as number) < 30) && (
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', backgroundColor: 'rgba(231, 76, 60, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h1" sx={{ fontSize: '3em', color: '#e74c3c', mb: 1 }}>😰</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e74c3c', mb: 0.5 }}>
                      {t.epokAnalysisUI.unhealthyStress}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>{t.epokAnalysisUI.harmingPerformance}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Dynamic symptoms description based on actual lowest scores */}
      {(() => {
        const categoryKeys = Object.keys(categoryNames);
        const worstCategories = Object.entries(categoryScores)
          .slice(0, 7)
          .map(([, score], index) => {
            const categoryKey = categoryKeys[index] || categoryKeys[0];
            return { categoryKey, score: score as number };
          })
          .filter(({ score }) => score < 60)
          .sort((a, b) => a.score - b.score) // Sort by lowest scores first
          .slice(0, 3) // Take the 3 worst
          .map(({ categoryKey, score }) => {
            const categoryName = t.categoryNames[categoryKey as keyof typeof t.categoryNames];
            const scoreDescription = getCategoryDescription(categoryKey, score);
            return `${categoryName}: ${scoreDescription} `;
          })
          .filter(Boolean);
        const topStrengthCategory = Object.entries(categoryScores)
          .slice(0, 7)
          .map(([, score], index) => {
            const categoryKey = categoryKeys[index] || categoryKeys[0];
            return { categoryKey, score: score as number };
          });
        topStrengthCategory.sort((a, b) => b.score - a.score); // Sort by highest scores first
        const topStrength = topStrengthCategory[0];
        const topStrengthName = t.categoryNames[topStrength.categoryKey as keyof typeof t.categoryNames];
        const topStrengthMessage = getCategoryDescription(topStrength.categoryKey, topStrength.score);
        const topWeaknessCategory = Object.entries(categoryScores)
          .slice(0, 7)
          .map(([, score], index) => {
            const categoryKey = categoryKeys[index] || categoryKeys[0];
            return { categoryKey, score: score as number };
          });
        topWeaknessCategory.sort((a, b) => a.score - b.score); // Sort by lowest scores first
        const topWeakness = topWeaknessCategory[0];
        const topWeaknessName = t.categoryNames[topWeakness.categoryKey as keyof typeof t.categoryNames];
        const topWeaknessMessage = getCategoryDescription(topWeakness.categoryKey, topWeakness.score);

        if (worstCategories.length > 0) {
          return (
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
              {t.epokAnalysisUI.topStrength.replace('{category}', topStrengthName)} {topStrengthMessage}&nbsp;
              {t.epokAnalysisUI.topWeakness.replace('{category}', topWeaknessName)} {topWeaknessMessage}
            </Typography>
            // <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
            //   {t.epokAnalysisUI.mainSymptoms.replace('{categories}', worstCategories.join(' '))}
            // </Typography>
          );
        }
        return null;
      })()}

      {/* Call to Action */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #3498db, #2980b9)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 4,
          textAlign: 'center',
          boxShadow: '0 8px 20px rgba(52, 152, 219, 0.3)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t.epokAnalysisUI.timeToRealize}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.6, mb: 3, opacity: 0.95 }}>
          {t.epokAnalysisUI.nowYouKnow}
          <br />
          <Box component="span" sx={{ color: '#ffd700', fontWeight: 'bold' }}>
            {t.epokAnalysisUI.changeStarts}
          </Box>
        </Typography>
      </Paper>

      {/* Categories Analysis */}
      <Typography 
        variant="h4" 
        component="h2" 
        sx={{ 
          color: '#34495e',
          mb: 4,
          ...(direction === 'ltr'
            ? { borderLeft: '5px solid #3498db', pl: 3 }
            : { borderRight: '5px solid #3498db', pr: 3 }
          ),
          fontWeight: 'bold'
        }}
      >
        {t.epokAnalysisUI.categoriesBreakdown}
      </Typography>


      <Grid container spacing={3}>
        {detailedAnalysis.length > 0 ? (
          detailedAnalysis.map((analysis) => (
            <Grid item xs={12} key={analysis.categoryCode}>
              <Card
                sx={{
                  ...getCategoryStyle(analysis.score),
                  border: '1px solid',
                  borderRadius: 3,
                  mb: 2
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: '#2c3e50',
                        flexGrow: 1
                      }}
                    >
                      {analysis.categoryName[language as keyof typeof analysis.categoryName]} - {(() => {
                      // Map category code to category key
                      const categoryCodeToKey: { [key: string]: string } = {
                        'A': 'management',
                        'B': 'organization', 
                        'C': 'repeatSales',
                        'D': 'finance',
                        'E': 'operations',
                        'F': 'qualityControl',
                        'G': 'newSales',
                        'H': 'stress',
                        'I': 'principles',
                        'J': 'helpAcceptance'
                      };
                      
                      const categoryKey = categoryCodeToKey[analysis.categoryCode];
                      return t.categoryDescriptions[categoryKey as keyof typeof t.categoryDescriptions];
                    })()}
                    </Typography>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: analysis.score >= 70 ? '#4caf50' : analysis.score >= 40 ? '#ff9800' : '#f44336'
                      }}
                    />
                  </Box>

                  {/* Category Description based on score */}
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3,                       
                      color: '#495057',
                      fontWeight: 'bold',
                    }}
                  >
                    {t.epokAnalysisUI.currentState} - 
                    {(() => {
                      // Map category code to category key
                      const categoryCodeToKey: { [key: string]: string } = {
                        'A': 'management',
                        'B': 'organization', 
                        'C': 'repeatSales',
                        'D': 'finance',
                        'E': 'operations',
                        'F': 'qualityControl',
                        'G': 'newSales',
                        'H': 'stress',
                        'I': 'principles',
                        'J': 'helpAcceptance'
                      };
                      
                      const categoryKey = categoryCodeToKey[analysis.categoryCode];
                      return getCategoryDescription(categoryKey, analysis.score);
                    })()}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip
                      size="small"
                      label={`${language === 'he' ? 'חזקות' : 'Strengths'}: ${analysis.positiveCount}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${language === 'he' ? 'פיתוח' : 'Development'}: ${analysis.neutralCount}`}
                      color="warning"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${language === 'he' ? 'שיפור' : 'Improvement'}: ${analysis.needsImprovementCount}`}
                      color="error"
                      variant="outlined"
                    />
                  </Box>

                  {/* Positive Comments (9s) */}
                  {analysis.positiveComments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ fontWeight: 'bold', color: '#27ae60', mb: 1 }}
                      >
                        💪 {language === 'he' ? 'נקודות חוזק' : 'Strengths'}
                      </Typography>
                      {analysis.positiveComments.map((comment, idx) => (
                        <Typography 
                          key={idx}
                          variant="body2" 
                          sx={{ mb: 0.5, pl: 2, color: '#2d5016' }}
                        >
                          • {comment}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Constructive Comments (0s) */}
                  {analysis.constructiveComments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <Typography 
                        variant="subtitle2" 
                        sx={{ fontWeight: 'bold', color: '#e74c3c', mb: 1 }}
                      >
                        🎯 {language === 'he' ? 'תחומים לשיפור' : 'Areas for Improvement'}
                      </Typography>
                      {analysis.constructiveComments.map((comment, idx) => (
                        <Typography 
                          key={idx}
                          variant="body2" 
                          sx={{ mb: 0.5, pl: 2, color: '#8b2635' }}
                        >
                          • {comment}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Neutral Comments (3s) */}
                  {analysis.neutralComments.length > 0 && (
                    <Box>
                      <Divider sx={{ my: 1 }} />
                      <Typography 
                        variant="subtitle2" 
                        sx={{ fontWeight: 'bold', color: '#f39c12', mb: 1 }}
                      >
                        🌱 {language === 'he' ? 'הזדמנויות לפיתוח' : 'Development Opportunities'}
                      </Typography>
                      {analysis.neutralComments.map((comment, idx) => (
                        <Typography 
                          key={idx}
                          variant="body2" 
                          sx={{ mb: 0.5, pl: 2, color: '#8b6914' }}
                        >
                          • {comment}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          // Fallback to original category display if no detailed analysis available
          categoryData.map((category, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  ...getCategoryStyle(category.value),
                  border: '1px solid',
                  borderRadius: 3,
                  height: '100%'
                }}
              >
                <CardContent>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#2c3e50',
                      mb: 2,
                      fontSize: '1.3em'
                    }}
                  >
                    {category.name}
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    {category.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Summary */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #e8f5e8 100%)',
          color: '#2c3e50',
          p: 4,
          borderRadius: 3,
          mt: 6,
          textAlign: language === 'he' ? 'right' : 'left',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
          {t.epokAnalysisUI.summary}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            {t.epokAnalysisUI.analysisConclusion}
          </Typography>
          <Typography variant="h6" sx={{ color: '#1976d2', mb: 2, fontWeight: 'bold' }}>
            {t.epokAnalysisUI.problemIdentification}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            {(() => {
              const categoryKeys = Object.keys(categoryNames);

              // Find the worst category from scores below 60
              const categoriesUnder60 = Object.entries(categoryScores)
                .slice(0, 7)
                .map(([, score], index) => {
                  const categoryKey = categoryKeys[index] || categoryKeys[0];
                  return { categoryKey, score: score as number, index };
                })
                .filter(({ score }) => score < 60)
                .sort((a, b) => a.score - b.score); // Sort by lowest scores first

              const worstCategoryName = categoriesUnder60.length > 0
                ? (() => {
                    const categoryKey = categoriesUnder60[0].categoryKey;
                    const categoryName = t.categoryNames[categoryKey as keyof typeof t.categoryNames];
                    const score = categoriesUnder60[0].score;
                    const scoreDescription = getCategoryDescription(categoryKey, score);
                    return `${categoryName}: ${scoreDescription} `;
                  })()
                : t.epokAnalysisUI.fallbackCategory;
              
              return (
                <>
                  {t.epokAnalysisUI.analysisBasedOn} <Box component="span" sx={{ fontWeight: 'bold' }}>{t.epokAnalysisUI.worstArea.replace('{category}', worstCategoryName)}</Box>. 
                  {t.epokAnalysisUI.mainProblemSource} 
                  <Box component="span" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    {t.epokAnalysisUI.improvementImpact}</Box>.
                </>
              );
            })()}
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
            {t.epokAnalysisUI.customSolution}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            {t.epokAnalysisUI.mainRecommendations}
          </Typography>
          <Box component="ul" sx={{ mt: 2, pr: 2 }}>
            {getTopRecommendations().map((rec, index) => (
              <Box component="li" key={index} sx={{ mb: 1, lineHeight: 1.6 }}>
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.6 }}>
            {t.epokAnalysisUI.problemNotCapital}, {t.epokAnalysisUI.problemIsKnowledge}
            <br />
            <Box component="span" sx={{ color: '#1976d2', fontSize: '1.2em' }}>
              {t.epokAnalysisUI.solutionFormula}
            </Box>
          </Typography>
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 6, pt: 4, borderTop: '3px solid #ecf0f1', textAlign: 'center', color: '#7f8c8d' }}>
        <Typography variant="body1">
          {t.epokAnalysisUI.analysisCreated} {new Date().toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t.epokAnalysisUI.methodology}
        </Typography>
        <Typography variant="body2">
          {t.epokAnalysisUI.copyright} {new Date().getFullYear()}
        </Typography>
        <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 'bold', mt: 1 }}>
          {t.epokAnalysisUI.criticalBusinessWarning}
        </Typography>
      </Box>
    </Box>
  );
};

export default EpokAnalysisView;